import type { SGWTConnectError } from '@sgwt/connect-core';
import { getConfig } from './config';
import { sgwtConnect } from './sgwtConnect';
import type { UserInfo } from './sgconnect.types';
import type { SgwtConnectHTMLElement } from '@sgwt/sgwt-widgets-react';

export const BUS_TOPIC_GLOBALLANGUAGE = 'global.language';
export const BUS_TOPIC_SGCONNECT_ACCESSTOKEN = 'sg-connect.access-token';
export const BUS_TOPIC_SGCONNECT_USERCONNECTION = 'sg-connect.user-connection';
export const BUS_TOPIC_SGCONNECT_USERINFO = 'sg-connect.user-info';
export const BUS_TOPIC_SGCONNECT_GRANTEDSCOPE = 'sg-connect.granted-scope';
export const TOPIC_SGCONNECT_READY = 'sgwt-connect--ready';


type BusEventCallback<T> = (payload: T | undefined) => void;
type SubscriptionHandle = any;

export interface SgWidgetsBus {
  dangerouslyGetCurrentValue<T>(topicName: string): T | undefined | null;
  subscribe<T>(topicName: string, cb: BusEventCallback<T>): SubscriptionHandle;
  unsubscribe(handle: SubscriptionHandle): void;
}

export function getWidgetBus(): SgWidgetsBus | null {
  const widgetConfiguration = (window as any).SGWTWidgetConfiguration;
  return widgetConfiguration?.bus ? widgetConfiguration.bus : null;
}

export function getWidget<T extends HTMLElement>(tagName: string) {
  return document.querySelector(tagName) as T;
}

export function getAuthorizationHeader(): string | null {
  return sgwtConnect.getAuthorizationHeader();
}

export function getAuthorizationError(): SGWTConnectError | null {
  return sgwtConnect.getAuthorizationError();
}

export function getAuthorizationToken(): string | undefined {
  const authorizationHeader = getAuthorizationHeader();
  return authorizationHeader?.replace('Bearer ', '');
}

export function useFakeAuthentication(): boolean {
  const config = getConfig();
  return (
    config.settings?.enableFakeAuthentication === true &&
    window.location.search.includes('fakeUlysseFlag')
  );
}


export function getUserInfo<UserPermissionName extends string>(): UserInfo<UserPermissionName> | undefined | null {
  const bus = getWidgetBus();

  return bus?.dangerouslyGetCurrentValue<UserInfo<UserPermissionName>>(BUS_TOPIC_SGCONNECT_USERINFO);
}

export function getIsUserFromLan(
  origin_network: string | undefined,
): boolean {
  return origin_network === 'LAN';
}

export function getAppropriateApiUrl(apiUrl: string, apiUrlForLan: string) {
  const userInfo = getUserInfo()

  const isUserFromLan = getIsUserFromLan(userInfo?.origin_network);

  return isUserFromLan ? apiUrlForLan : apiUrl

}

export interface SgConnectUserInfo {
  name: string;
  mail: string;
  sesame_id: string;
  origin_network: string;
}

export interface SgwtWidgetBusSubscribers {
  onUserInfo?: (userinfo: SgConnectUserInfo | undefined) => void;
}


const subscribeToSGWTWidgetBus = ({ onUserInfo }: SgwtWidgetBusSubscribers): void => {
  if (onUserInfo) {
    window.SGWTWidgetConfiguration.bus.subscribe(BUS_TOPIC_SGCONNECT_USERINFO, onUserInfo);
  }
};

export const setupSgwtConnectWidget = (
  subscribers: SgwtWidgetBusSubscribers,
  sgwtConnectInstance = sgwtConnect,
): void => {
  const widget = document.querySelector<SgwtConnectHTMLElement>('sgwt-connect');
  if (widget) {
    // When the code is executed, the widget may not have been initialized. So, we need to check that
    if (typeof widget.setSgwtConnectInstance === 'undefined') {
      // Widget is not initialized yet, so we will wait the event that indicates the widget is ready...
      const handleSgwtConnectReady = (): void => {
        widget.setSgwtConnectInstance(sgwtConnectInstance);
        subscribeToSGWTWidgetBus(subscribers);
        // Remove the event listener
        widget.removeEventListener(TOPIC_SGCONNECT_READY, handleSgwtConnectReady);
      };
      widget.addEventListener(TOPIC_SGCONNECT_READY, handleSgwtConnectReady);
    } else {
      // Widget is initialized...
      widget.setSgwtConnectInstance(sgwtConnectInstance);
      subscribeToSGWTWidgetBus(subscribers);
    }
  }
};
